import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import moment from "moment";
import PageContainer from "../../layouts/PageContainer";
import Loader from "../../layouts/Loader";
import { helpers } from "../../helper/helper";
import {
  OVERVIEW_HELP,
  TOGGLE_HELP,
  SIGNAL_HELP,
  NUMBER_HELP,
} from "./algorithmHelp";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: 10,
  },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 600,
  },
  hint: {
    color: "#6b7480",
    fontSize: 13,
    marginBottom: theme.spacing(2),
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
  },
  signalRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  info: {
    padding: 4,
    marginLeft: 2,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginTop: theme.spacing(2),
  },
  meta: {
    fontSize: 13,
    color: "#6b7480",
    marginTop: theme.spacing(1),
  },
  tableWrap: {
    overflowX: "auto",
  },
  helpLabel: {
    fontWeight: 600,
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "#0b6e7f",
    marginBottom: 4,
  },
  helpText: {
    fontSize: 15,
    lineHeight: 1.6,
    marginBottom: theme.spacing(2.5),
  },
  overviewBtn: {
    marginLeft: "auto",
  },
}));

const SIGNALS = [
  { key: "view", label: "Views", hint: "How often it was watched." },
  { key: "like", label: "Likes", hint: "Cheap to give, so usually worth less than a comment." },
  { key: "comment", label: "Comments", hint: "Costs effort, so a stronger signal than a like." },
  { key: "share", label: "Shares", hint: "Usually the strongest sign of genuine quality." },
  { key: "starPoint", label: "Star points earned", hint: "Star points this content itself earned." },
  { key: "popularity", label: "Popularity earned", hint: "Popularity this content itself earned." },
  { key: "creatorPopularity", label: "Creator popularity", hint: "The creator's lifetime popularity. High values favour established accounts." },
  { key: "creatorStarPoint", label: "Creator star points", hint: "The creator's lifetime star points." },
];

const NUMBERS = [
  { key: "gravity", label: "Freshness decay", hint: "0 ignores age. 1.2 is balanced. 1.8 buries yesterday." },
  { key: "pinBoost", label: "Pinned bonus", hint: "Flat bonus for items pinned with 'select top'." },
  { key: "explorationPercent", label: "New content share (%)", hint: "Portion of each page kept for content few people have seen yet." },
  { key: "pageSize", label: "Items per page", hint: "How many items one feed request returns." },
  { key: "seenWindowDays", label: "Repeat after (days)", hint: "How long before something watched can appear again." },
  { key: "servedWindowHours", label: "Suppress shown for (hours)", hint: "How long something merely shown is held back." },
  { key: "refreshMinutes", label: "Rescore every (minutes)", hint: "How often points are recalculated from fresh engagement." },
  { key: "locationRadiusKm", label: "Nearby radius (km)", hint: "How far away still counts as nearby." },
  { key: "locationPercent", label: "Nearby share of page (%)", hint: "How much of each page is nearby content. The rest is the normal feed." },
  { key: "locationBoost", label: "Nearby priority", hint: "How hard nearby content is pushed up. Stays below pinned." },
  { key: "revivalWindowDays", label: "Comeback window (days)", hint: "How recent the activity must be to count as a comeback." },
  { key: "maxPerCreatorPerPage", label: "Max per creator, per page", hint: "How many items from the same person can share one page." },
  { key: "oldContentPercent", label: "Older content share of page (%)", hint: "How much of each page is older content." },
  { key: "oldContentAfterDays", label: "Counts as older after (days)", hint: "How old something must be to count towards that share." },
];

const TOGGLES = [
  { key: "enabled", label: "Use this algorithm", hint: "Off returns the feed to its previous ordering immediately." },
  { key: "antiRepeatEnabled", label: "Do not repeat content", hint: "Remembers what each user has already been shown." },
  { key: "rankFollowingFeed", label: "Also rank the following feed", hint: "Off leaves the following feed close to newest-first." },
  { key: "pinnedEnabled", label: "Use pinned positions", hint: "Off ignores pinning; your pins stay saved." },
  { key: "locationEnabled", label: "Show nearby content first", hint: "Lifts content from people near the viewer to the top." },
  { key: "revivalEnabled", label: "Let older content come back", hint: "Recent views, likes or star points give old content a fresh chance." },
  { key: "diversityEnabled", label: "Mix up creators", hint: "Stops one person filling the page when their content is approved together." },
  { key: "oldContentEnabled", label: "Always show some older content", hint: "Keeps a share of every page for older reels and posts." },
];

const AlgorithmConfig = ({ feedType, heading }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [config, setConfig] = useState(null);
  const [meta, setMeta] = useState({ rankedCount: 0, lastBuiltAt: null });
  const [preview, setPreview] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [help, setHelp] = useState(null);

  const notify = useCallback(
    (message, variant) =>
      enqueueSnackbar(message, { variant, autoHideDuration: 3000 }),
    [enqueueSnackbar]
  );

  /** The little (i) next to a label. */
  const InfoButton = ({ topic, label }) => (
    <Tooltip title={`What does "${label}" do?`}>
      <IconButton
        className={classes.info}
        size="small"
        aria-label={`Explain ${label}`}
        onClick={() => setHelp(topic)}
      >
        <InfoOutlinedIcon fontSize="small" color="primary" />
      </IconButton>
    </Tooltip>
  );

  const loadConfig = useCallback(async () => {
    setLoading(true);
    const result = await helpers.apiCall("GET", `feed-algorithm/${feedType}/config/v1`);
    if (result?.success) {
      setConfig(result.data.config);
      setMeta({ rankedCount: result.data.rankedCount, lastBuiltAt: result.data.lastBuiltAt });
    } else {
      notify(result?.message || "Could not load the algorithm settings", "error");
    }
    setLoading(false);
  }, [feedType, notify]);

  const loadPreview = useCallback(async () => {
    const result = await helpers.apiCall("GET", `feed-algorithm/${feedType}/preview/v1?limit=20`);
    if (result?.success) setPreview(result.data.rows || []);
  }, [feedType]);

  useEffect(() => {
    loadConfig();
    loadPreview();
  }, [loadConfig, loadPreview]);

  const setSignal = (key, field, value) =>
    setConfig((prev) => ({
      ...prev,
      signals: { ...prev.signals, [key]: { ...prev.signals[key], [field]: value } },
    }));

  const setField = (key, value) => setConfig((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      enabled: config.enabled,
      antiRepeatEnabled: config.antiRepeatEnabled,
      rankFollowingFeed: config.rankFollowingFeed,
      pinnedEnabled: config.pinnedEnabled,
      locationEnabled: config.locationEnabled,
      revivalEnabled: config.revivalEnabled,
      diversityEnabled: config.diversityEnabled,
      oldContentEnabled: config.oldContentEnabled,
      signals: config.signals,
      gravity: Number(config.gravity),
      pinBoost: Number(config.pinBoost),
      explorationPercent: Number(config.explorationPercent),
      pageSize: Number(config.pageSize),
      seenWindowDays: Number(config.seenWindowDays),
      servedWindowHours: Number(config.servedWindowHours),
      refreshMinutes: Number(config.refreshMinutes),
      locationRadiusKm: Number(config.locationRadiusKm),
      locationPercent: Number(config.locationPercent),
      locationBoost: Number(config.locationBoost),
      revivalWindowDays: Number(config.revivalWindowDays),
      maxPerCreatorPerPage: Number(config.maxPerCreatorPerPage),
      oldContentPercent: Number(config.oldContentPercent),
      oldContentAfterDays: Number(config.oldContentAfterDays),
    };

    const result = await helpers.apiCall("PUT", `feed-algorithm/${feedType}/config/v1`, payload);
    if (result?.success) {
      setMeta({ rankedCount: result.data.rankedCount, lastBuiltAt: result.data.lastBuiltAt });
      notify("Saved. Points recalculated for every item.", "success");
      loadPreview();
    } else {
      notify(result?.message || "Could not save", "error");
    }
    setSaving(false);
  };

  const handleRebuild = async () => {
    setSaving(true);
    const result = await helpers.apiCall("POST", `feed-algorithm/${feedType}/rebuild/v1`);
    if (result?.success) {
      setMeta({ rankedCount: result.data.rankedCount, lastBuiltAt: result.data.lastBuiltAt });
      notify("Points recalculated", "success");
      loadPreview();
    } else {
      notify(result?.message || "Could not rebuild", "error");
    }
    setSaving(false);
  };

  if (loading || !config) {
    return (
      <PageContainer pageheading={heading}>
        <Loader />
      </PageContainer>
    );
  }

  return (
    <PageContainer pageheading={heading}>
      <Paper className={classes.paper} elevation={3}>
        <div className={classes.sectionHead}>
          <Typography className={classes.sectionTitle}>Switches</Typography>
          <Button
            className={classes.overviewBtn}
            color="primary"
            startIcon={<HelpOutlineIcon />}
            onClick={() => setHelp(OVERVIEW_HELP)}
          >
            How does this work?
          </Button>
        </div>
        <Grid container spacing={2}>
          {TOGGLES.map((toggle) => (
            <Grid item md={4} xs={12} key={toggle.key}>
              <div className={classes.labelRow}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={!!config[toggle.key]}
                      onChange={(e) => setField(toggle.key, e.target.checked)}
                    />
                  }
                  label={toggle.label}
                />
                <InfoButton topic={TOGGLE_HELP[toggle.key]} label={toggle.label} />
              </div>
              <div className={classes.hint}>{toggle.hint}</div>
            </Grid>
          ))}
        </Grid>

        <Divider style={{ margin: "24px 0" }} />

        <Typography className={classes.sectionTitle}>What counts, and how much</Typography>
        <div className={classes.hint}>
          A higher weight pulls content with that signal further up. Turn a signal off to
          drop it from the calculation entirely. Press the <b>i</b> beside any of them for a
          fuller explanation.
        </div>
        <Grid container spacing={2}>
          {SIGNALS.map((signalDef) => {
            const signal = config.signals?.[signalDef.key] || { enabled: false, weight: 0 };
            return (
              <Grid item md={6} xs={12} key={signalDef.key}>
                <div className={classes.signalRow}>
                  <Switch
                    color="primary"
                    checked={!!signal.enabled}
                    onChange={(e) => setSignal(signalDef.key, "enabled", e.target.checked)}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    variant="outlined"
                    size="small"
                    label={signalDef.label}
                    value={signal.weight}
                    disabled={!signal.enabled}
                    inputProps={{ step: "0.1", min: "0" }}
                    onChange={(e) => setSignal(signalDef.key, "weight", e.target.value)}
                  />
                  <InfoButton topic={SIGNAL_HELP[signalDef.key]} label={signalDef.label} />
                </div>
                <div className={classes.hint}>{signalDef.hint}</div>
              </Grid>
            );
          })}
        </Grid>

        <Divider style={{ margin: "24px 0" }} />

        <Typography className={classes.sectionTitle}>Tuning</Typography>
        <div className={classes.hint}>
          These control how the feed feels overall rather than what counts towards points.
        </div>
        <Grid container spacing={2}>
          {NUMBERS.map((field) => (
            <Grid item md={4} xs={12} key={field.key}>
              <div className={classes.signalRow}>
                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  size="small"
                  label={field.label}
                  value={config[field.key]}
                  inputProps={{ step: "0.1", min: "0" }}
                  onChange={(e) => setField(field.key, e.target.value)}
                />
                <InfoButton topic={NUMBER_HELP[field.key]} label={field.label} />
              </div>
              <div className={classes.hint}>{field.hint}</div>
            </Grid>
          ))}
        </Grid>

        <div className={classes.actions}>
          <Button variant="contained" color="primary" disabled={saving} onClick={handleSave}>
            {saving ? "Please wait..." : "Save and recalculate"}
          </Button>
          <Button variant="outlined" color="primary" disabled={saving} onClick={handleRebuild}>
            Recalculate points only
          </Button>
        </div>
        <div className={classes.meta}>
          {meta.rankedCount} items ranked
          {meta.lastBuiltAt
            ? ` · last calculated ${moment(new Date(meta.lastBuiltAt)).format("DD-MM-YYYY h:mm a")}`
            : " · not calculated yet"}
        </div>
      </Paper>

      <Paper className={classes.paper} elevation={3}>
        <Typography className={classes.sectionTitle}>Top 20 with these settings</Typography>
        <div className={classes.hint}>
          What people would see first right now. Save your changes, then check this before
          trusting them.
        </div>
        <div className={classes.tableWrap}>
          <table className="table table-striped" style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th>#</th>
                <th>Points</th>
                <th>Creator</th>
                <th>Views</th>
                <th>Likes</th>
                <th>Shares</th>
                <th>Star points</th>
                <th>Popularity</th>
                <th>Posted</th>
              </tr>
            </thead>
            <tbody>
              {preview.length === 0 ? (
                <tr>
                  <td colSpan={9}>No points calculated yet.</td>
                </tr>
              ) : (
                preview.map((row) => (
                  <tr key={row._id}>
                    <td>{row.rank}</td>
                    <td>
                      <b>{row.score}</b>
                      {row.isSelectedInTop ? " (pinned)" : ""}
                    </td>
                    <td>{row.creatorName || "-"}</td>
                    <td>{row.views}</td>
                    <td>{row.likes}</td>
                    <td>{row.share}</td>
                    <td>{row.starPoints}</td>
                    <td>{row.popularity}</td>
                    <td>
                      {row.createdAt
                        ? moment(new Date(row.createdAt)).format("DD-MM-YYYY")
                        : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Paper>

      <Dialog
        open={!!help}
        onClose={() => setHelp(null)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        aria-labelledby="algorithm-help-title"
      >
        <DialogTitle id="algorithm-help-title">{help?.title}</DialogTitle>
        <DialogContent dividers>
          {(help?.sections || []).map((part) => (
            <div key={part.label}>
              <div className={classes.helpLabel}>{part.label}</div>
              <div className={classes.helpText}>{part.text}</div>
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelp(null)} color="primary" variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default AlgorithmConfig;
